/**
 * SB6-ANA-CACHE-03 — the thin seam between the byte-level image cache
 * (`imageCache.ts`, CACHE-01) and the dataset-level manifest + LRU evictor.
 *
 * `imageCache.put` calls `recordCachedKey(key, bytes)` for every blob it stores.
 * This module attributes the bytes to a dataset (via the canonical key), records
 * them in the manifest, and — when a genuinely-new image is recorded — fires the
 * LRU evictor (which itself decides whether any budget ceiling is crossed and
 * never evicts the currently-open dataset).
 *
 * It lives apart from `imageCache.ts` so that file stays a pure Cache-Storage
 * wrapper with one cheap call-out (no manifest/eviction import cycle). The
 * "currently-open acquisition" (eviction-exempt, AC7) is pushed in by the
 * analysis host (`usePrefetch`) rather than pulled from the prefetch scheduler,
 * so this seam has no upward dependency on the queue.
 */
import { acquisitionIdFromKey, cacheManifest } from "./cacheManifest.js";
import { triggerEviction } from "./lruEviction.js";

/** The acquisition currently open in the analysis surface (eviction-exempt). */
let openAcquisitionId: string | null = null;

/** Set by the analysis host on dataset open; cleared on unmount. */
export function setOpenAcquisitionId(id: string | null): void {
  openAcquisitionId = id;
}

/** Read the currently-open acquisition id (the LRU evictor never removes it). */
export function getOpenAcquisitionId(): string | null {
  return openAcquisitionId;
}

/**
 * Record a freshly-cached image into its dataset's manifest, then evaluate the
 * LRU budget. Best-effort: the manifest write is fire-and-forget into
 * IndexedDB; eviction is fire-and-forget. Never throws.
 */
export function recordCachedKey(key: string, bytes: number): void {
  const id = acquisitionIdFromKey(key);
  if (!id) return;
  const before = cacheManifest.get(id);
  const wasKnown = before?.imageKeys.includes(key) ?? false;
  cacheManifest.recordCachedImage(id, key, bytes);
  // Only a genuinely-new cached image can push the footprint over budget; a
  // recency re-touch can't. Keep eviction off the no-op path.
  if (wasKnown) return;
  triggerEviction(openAcquisitionId);
}

/**
 * Touch a dataset's last-access recency on a cache READ (throttled inside the
 * manifest). Best-effort; never throws.
 */
export function touchKeyAccess(key: string): void {
  const id = acquisitionIdFromKey(key);
  if (!id) return;
  cacheManifest.touch(id);
}
